import { useCallback } from "react";
import { useAppContext } from "../../../../context/AppContext";

// Country phone number length mapping (min and max digits)
const COUNTRY_PHONE_LENGTHS: { [key: string]: { min: number; max: number } } = {
  "United States": { min: 10, max: 10 },
  "Canada": { min: 10, max: 10 },
  "United Kingdom": { min: 9, max: 10 },
  "Australia": { min: 9, max: 9 },
  "Germany": { min: 10, max: 11 },
  "France": { min: 9, max: 9 },
  "Italy": { min: 9, max: 10 },
  "Japan": { min: 10, max: 10 },
  "China": { min: 11, max: 11 },
  "Brazil": { min: 10, max: 11 },
  "Russia": { min: 10, max: 10 },
  "Mexico": { min: 10, max: 10 },
  "Spain": { min: 9, max: 9 },
  "South Korea": { min: 10, max: 11 },
  "United Arab Emirates": { min: 9, max: 9 },
  "Saudi Arabia": { min: 9, max: 9 },
  "Singapore": { min: 8, max: 8 },
  "South Africa": { min: 9, max: 9 },
  "New Zealand": { min: 8, max: 10 },
  "Albania": { min: 8, max: 9 },
  "Austria": { min: 10, max: 13 },
  "Belgium": { min: 8, max: 10 },
  "Bosnia-Herzegovina": { min: 8, max: 9 },
  "Botswana": { min: 7, max: 8 },
  "Bulgaria": { min: 8, max: 9 },
  "Croatia": { min: 8, max: 9 },
  "Cyprus": { min: 8, max: 8 },
  "Czech Republic": { min: 9, max: 9 },
  "Denmark": { min: 8, max: 8 },
  "Estonia": { min: 7, max: 8 },
  "Finland": { min: 5, max: 12 },
  "French Polynesia": { min: 6, max: 8 },
  "Ghana": { min: 9, max: 9 },
  "Greece": { min: 10, max: 10 },
  "Hungary": { min: 8, max: 9 },
  "Iceland": { min: 7, max: 9 },
  "Ireland": { min: 7, max: 9 },
  "Israel": { min: 8, max: 9 },
  "Kosovo": { min: 8, max: 8 },
  "Latvia": { min: 8, max: 8 },
  "Lesotho": { min: 8, max: 8 },
  "Lithuania": { min: 8, max: 8 },
  "Macedonia": { min: 8, max: 8 },
  "Malta": { min: 8, max: 8 },
  "Moldova": { min: 8, max: 8 },
  "Montenegro": { min: 8, max: 8 },
  "Namibia": { min: 9, max: 9 },
  "Netherlands": { min: 9, max: 9 },
  "North Macedonia": { min: 8, max: 8 },
  "Norway": { min: 8, max: 8 },
  "Poland": { min: 9, max: 9 },
  "Portugal": { min: 9, max: 9 },
  "Romania": { min: 9, max: 9 },
  "Serbia": { min: 8, max: 9 },
  "Slovak Republic": { min: 9, max: 9 },
  "Slovakia": { min: 9, max: 9 },
  "Slovenia": { min: 8, max: 8 },
  "Swaziland": { min: 8, max: 8 },
  "Eswatini": { min: 8, max: 8 },
  "Sweden": { min: 7, max: 13 },
  "Switzerland": { min: 9, max: 9 },
  "Turkey": { min: 10, max: 10 },
  "Ukraine": { min: 9, max: 9 },
};

/**
 * Custom hook for form validation functions
 * Provides validation functions for email, password, phone number, and required fields
 */
export const useFormValidation = () => {
  const { getTranslation } = useAppContext();

  const validateEmail = useCallback(
    (email: string): string | null => {
      if (!email.trim()) {
        return getTranslation("Txt7");
      }
      if (!/^\S+@\S+\.\S+$/.test(email)) {
        return getTranslation("Txt713");
      }
      // Check if email contains letters (not just numbers)
      if (!/[a-zA-Z]/i.test(email)) {
        return getTranslation("Txt713");
      }
      return null;
    },
    [getTranslation]
  );

  // Basic password validation for signup
  const validatePassword = useCallback(
    (password: string): string | null => {
      if (!password) {
        return getTranslation("Err22");
      }
      if (password.length < 6) {
        return getTranslation("Err24");
      }
      return null;
    },
    [getTranslation]
  );

  // Strong password validation with complexity requirements
  const validateStrongPassword = useCallback(
    (password: string): string | null => {
      if (!password) {
        return getTranslation("Err22");
      }
      if (password.length < 6) {
        return getTranslation("Err24");
      }
      
      // // Check for uppercase, lowercase, number, and special character
      // const hasUppercase = /[A-Z]/.test(password);
      // const hasLowercase = /[a-z]/.test(password);
      // const hasNumber = /\d/.test(password);
      // const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
      
      // if (!hasUppercase || !hasLowercase || !hasNumber || !hasSpecialChar) {
      //   return getTranslation("Err25");
      // }
      
      return null;
    },
    [getTranslation]
  );

  // Password confirmation validation
  const validatePasswordConfirmation = useCallback(
    (password: string, confirmPassword: string): string | null => {
      if (!confirmPassword) {
        return getTranslation("Err22");
      }
      if (password !== confirmPassword) {
        return getTranslation("Err26");
      }
      return null;
    },
    [getTranslation]
  );

  // Login password validation (basic check)
  const validateLoginPassword = useCallback(
    (password: string): string | null => {
      if (!password) {
        return getTranslation("Err22");
      }
      return null;
    },
    [getTranslation]
  );

  const validateRequired = useCallback(
    (value: string): string | null => {
      if (!value.trim()) {
        return getTranslation("Txt6");
      }
      return null;
    },
    [getTranslation]
  );

  const validateRequiredfirst = useCallback(
    (value: string): string | null => {
      if (!value.trim()) {
        return getTranslation("Txt709");
      }
      return null;
    },
    [getTranslation]
  );

  const validateRequiredlast = useCallback(
    (value: string): string | null => {
      if (!value.trim()) {
        return getTranslation("Txt711");
      }
      return null;
    },
    [getTranslation]
  );

  const validateRequiredcountry = useCallback(
    (value: string): string | null => {
      if (!value.trim()) {
        return getTranslation("Txt894") + "*"
      }
      return null;
    },
    [getTranslation]
  );

  const validatePhoneNumber = useCallback(
    (value: string, countryCode?: string): string | null => {
      const trimmed = value.trim();
      if (!trimmed) {
        return getTranslation("Txt949");
      }
      const normalized = trimmed.replace(/\D/g, "");
      
      // If country is selected, validate against country-specific length
      if (countryCode && COUNTRY_PHONE_LENGTHS[countryCode]) {
        const { min, max } = COUNTRY_PHONE_LENGTHS[countryCode];
        if (normalized.length < min || normalized.length > max) {
            return `${getTranslation("Txt950")} ${min}${min !== max ? `-${max}` : ''} ${getTranslation("Txt951")}`;
          }
      } else {
        // Default validation: 10 digits for any other country
        if (normalized.length !== 10) {
          return `${getTranslation("Txt950")} 10 ${getTranslation("Txt951")}`;
        }
      }
      return null;
    },
    [getTranslation]
  );

  return { 
    validateEmail, 
    validatePassword, 
    validateStrongPassword, 
    validatePasswordConfirmation, 
    validateLoginPassword, 
    validateRequired,
    validateRequiredfirst,
    validateRequiredlast,
    validateRequiredcountry,
    validatePhoneNumber
  };
};
